import { trans, useSoyuzFlag } from '@spotahome/soyuz/client';
import PropTypes from 'prop-types';

import { useIsUserLogged } from '@spotahome/ui-library';
import { ListingShape } from '@spotahome/marketplace-common/src/shapes/listing';
import ListingContent from '@spotahome/marketplace-common/src/components/ListingContent';

import OtherRooms from './OtherRooms';
import CanIVisitButton from './CanIVisit';
import ListingDetailsFeatures from './ListingDetailsFeatures';
import { ListingSectionObservable } from './ListingSection';
import ListingTitle from './ListingTitle';
import ListingEnergyCertificate from './ListingEnergyCertificate';
import ListingDescription from './ListingDescription';

import './ListingDetails.scss';

const DETAILS_KEY = 'property_show.title.details';
const PROPERTY_KEY = 'property_show.title.apartment_features';
const ROOM_KEY = 'property_show.title.room_in_apartment';

const ListingDetails = ({
  description = '',
  landlordDescription = '',
  details = '',
  isRoomsAggregator,
  isWholeApartment,
  listingId,
  propertyFeatures,
  propertyFeaturesOldModel,
  roomsInApartment,
  listing,
  locale,
  roomDescription = '',
  hasRoomDescriptionPricing = false
}) => {
  const showLandlordDescription = useSoyuzFlag(
    'ShowLandlordDescription'
  ).isOn();

  const showDescription = roomDescription && !hasRoomDescriptionPricing;

  const isUserLogged = useIsUserLogged();

  const { energyCertificate, emissionsCertificate } = propertyFeatures || {};

  const hasMoreRooms = listing?.otherRooms.length && !isWholeApartment;

  const renderEveryRoomFeatures = () =>
    roomsInApartment.map((room, i) => (
      <ListingDetailsFeatures
        features={room.features}
        key={room.id}
        title={`${trans(ROOM_KEY)} ${i + 1}`}
      />
    ));

  const renderCurrentRoomFeatures = () => {
    // TO-DO: Not sure why we have to extract listing info from a room in apartment.
    // Better move this to GraphQL to expose these features in the ListingType

    const index = roomsInApartment.findIndex(room => room.id === listingId);
    const currentRoom = roomsInApartment[index];

    const getBedroomNumber = (room, roomIndex) => {
      if (room.floorplanReference) {
        return room.floorplanReference;
      }

      return room.bedroomNumber || roomIndex + 1;
    };

    return currentRoom ? (
      <ListingDetailsFeatures
        features={currentRoom.features}
        title={trans('property_show.title.your_room_in_apartment', {
          roomNumber: getBedroomNumber(currentRoom, index)
        })}
      />
    ) : null;
  };

  return (
    <ListingSectionObservable section="overview">
      <div className="listing-details">
        <ListingTitle role="secondary">
          {trans('listing.overview.title')}
        </ListingTitle>

        {!isRoomsAggregator && !isWholeApartment && renderCurrentRoomFeatures()}

        {propertyFeaturesOldModel && (
          <ListingDetailsFeatures
            title={trans(PROPERTY_KEY)}
            features={propertyFeaturesOldModel}
          />
        )}

        {!isRoomsAggregator && isWholeApartment && renderEveryRoomFeatures()}

        {(!!energyCertificate || !!details) && (
          <div className="listing-details__listing-content">
            <ListingTitle role="tertiary">{trans(DETAILS_KEY)}</ListingTitle>
            {(energyCertificate || emissionsCertificate) && (
              <div className="listing-content">
                <ListingEnergyCertificate
                  emissionsCertificate={emissionsCertificate}
                  energyCertificate={energyCertificate}
                />
              </div>
            )}
            {details && <ListingContent content={details} />}
          </div>
        )}
        <div className="listing-details__can-visit">
          <CanIVisitButton isChecked={listing && listing.checked} />
        </div>

        {showLandlordDescription && landlordDescription && (
          <ListingDescription
            className="listing-details__listing-description"
            title={trans('listing.overview.landlord.description.title')}
            description={landlordDescription}
          />
        )}

        {description && (
          <ListingDescription
            className="listing-details__listing-description"
            title={
              showLandlordDescription &&
              trans('listing.overview.description.title')
            }
            description={description}
            collapsible
          />
        )}
        {showDescription && (
          <ListingDescription
            className="listing-details__listing-description"
            title={trans('property_show.title.room_description')}
            description={roomDescription}
            collapsible
          />
        )}

        {!!hasMoreRooms && (
          <div data-slider="other-rooms">
            <ListingTitle role="tertiary">
              {trans('listing.other-rooms.title')}
            </ListingTitle>
            <OtherRooms
              otherRooms={listing.otherRooms}
              mainListing={listing}
              locale={locale}
              isLogged={isUserLogged}
            />
          </div>
        )}
      </div>
    </ListingSectionObservable>
  );
};

ListingDetails.propTypes = {
  listing: PropTypes.shape(ListingShape).isRequired,
  description: PropTypes.string,
  landlordDescription: PropTypes.string,
  details: PropTypes.string,
  isRoomsAggregator: PropTypes.bool.isRequired,
  isWholeApartment: PropTypes.bool.isRequired,
  listingId: PropTypes.string.isRequired,
  propertyFeaturesOldModel: PropTypes.shape({
    [PropTypes.string]: PropTypes.oneOfType([PropTypes.string, PropTypes.bool])
  }).isRequired,
  propertyFeatures: PropTypes.shape({
    [PropTypes.string]: PropTypes.shape({
      value: PropTypes.string
    })
  }).isRequired,
  roomsInApartment: PropTypes.arrayOf(
    PropTypes.shape({
      floorplanReference: PropTypes.number,
      features: PropTypes.shape({}),
      bedroomNumber: PropTypes.number
    })
  ).isRequired,
  locale: PropTypes.string.isRequired,
  roomDescription: PropTypes.string,
  hasRoomDescriptionPricing: PropTypes.bool
};

export default ListingDetails;
